// src/components/shared/modals/PlanClearModal.tsx
// Modal for confirming plan clear operation.
import { useState, useEffect, type FormEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import BaseModal from './BaseModal';
import ModalFooter from './ModalFooter';

export interface PlanClearTarget {
  plan_name: string;
  planNumber?: number | string;
  future_courses_count?: number;
  total_courses?: number;
  total_credits_planned?: number;
  total_credits?: number;
  [key: string]: unknown;
}

export interface PlanClearModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  plan: PlanClearTarget | null | undefined;
  isLoading?: boolean;
}

const PlanClearModal = ({
  isOpen,
  onClose,
  onConfirm,
  plan,
  isLoading = false,
}: PlanClearModalProps) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setConfirmText('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (confirmText !== 'CLEAR') {
      setError('You must type "CLEAR" exactly to confirm.');
      return;
    }

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to clear plan');
    }
  };

  if (!plan) return null;

  const planDisplayName = plan.plan_name.startsWith('Plan ')
    ? plan.plan_name
    : `Plan ${plan.planNumber || ''} · ${plan.plan_name}`;

  const courseCount = plan.future_courses_count || plan.total_courses || 0;
  const totalCredits = plan.total_credits_planned || plan.total_credits || 0;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Clear Plan"
      subtitle={planDisplayName}
      icon={{ Component: AlertTriangle, bgColor: 'bg-status-critical/15', color: 'text-status-critical' }}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit}>
        <div className="p-6">
          <div className="bg-status-critical/10 border border-status-critical/25 rounded-card p-4 mb-4">
            <p className="text-sm text-red-800 font-medium mb-2">
              This will permanently remove:
            </p>
            <ul className="text-sm text-red-700 space-y-1 ml-4 list-disc">
              <li>{courseCount} courses from all semesters</li>
              <li>{totalCredits} planned credits</li>
              <li>All semester planning data</li>
            </ul>
          </div>

          <div className="bg-status-progress/10 border border-status-progress/25 rounded-card p-4 mb-4">
            <p className="text-sm text-blue-800 font-medium mb-1">
              What will be preserved:
            </p>
            <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
              <li>Majors and minors</li>
              <li>Semester structure</li>
              <li>Plan name</li>
            </ul>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-mono bg-surface-sunken px-1 rounded">CLEAR</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e.target.value);
                setError(null);
              }}
              placeholder="CLEAR"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={isLoading}
              autoFocus
            />
          </div>

          {error && (
            <div className="mt-3 p-3 bg-status-critical/10 border border-status-critical/25 rounded-card">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        <ModalFooter
          onCancel={onClose}
          submitText="Clear Plan"
          loadingText="Clearing..."
          variant="red"
          isLoading={isLoading}
          disabled={confirmText !== 'CLEAR'}
          submitType="submit"
        />
      </form>
    </BaseModal>
  );
};

export default PlanClearModal;
